import { IWidgetBlockSettings } from '.';

export interface ThreatMatrixSettings extends IWidgetBlockSettings {
    title: string;
    timeRange: string;
    theme: 'light' | 'dark';
    rows: (CfjsonRow | TafRow)[];
    mode: 'cozy' | 'compact';
}

interface Row {
    id: string;
    type?: 'cfjson' | 'taf';
    label?: string;
    icon?: string;
}

export interface CfjsonRow extends Row {
    type?: 'cfjson';
    datasetId: string | undefined;
    space: {
        lat: number;
        lon: number;
    };
    capabilityId: string | undefined;
    obs?: {
        id: string;
        capabilityId?: string;
    };
    unit?: string;
    unitLabel?: string;
    warningThreshold: number;
    alertThreshold: number;
    thresholdOperator: 'greaterThan' | 'lessThan';
}

export interface TafRow extends Row {
    type: 'taf';
    icaoId?: string;
    element?: TAFElementKey;
    icon?: string;

    cdsTafVariables?: CdsTafVariable[];
}

export type TAFElementKey = 'Cloud' | 'Visibility' | 'Windspeed' | 'Windgust' | 'Crosswind' | 'Lightning';
export const tafElementKeys = ['Cloud', 'Visibility', 'Windspeed', 'Windgust', 'Crosswind', 'Lightning'] as const;

type CdsTafVariableKey = 'CloudBaseFt' | 'CloudCoverOktas' | 'VisibilityM' | 'WindSpeedKt' | 'WindGustKt' | 'CrossWindKt' | 'LightningPerc';

export interface CdsTafVariable {
    unit?: string; // cloudCoverOktas has no unit, uses number between 0 and 8
    warning: number;
    alert: number;
    operator: 'greaterThan' | 'lessThan';
    cdsTafVariableKey: CdsTafVariableKey;
    cdsTafVariableLabel: string;
}

interface Element {
    icon: string;
    primaryCdsTafVariableKey: CdsTafVariableKey;
    cdsTafVariables: CdsTafVariable[];
}

export const elements: { [Key in TAFElementKey]: Element } = {
    Cloud: {
        primaryCdsTafVariableKey: 'CloudBaseFt',
        icon: 'cloud',
        cdsTafVariables: [
            {
                unit: 'ft',
                warning: 1000,
                alert: 500,
                operator: 'lessThan',
                cdsTafVariableKey: 'CloudBaseFt',
                cdsTafVariableLabel: 'CloudBase',
            },
            {
                unit: undefined,
                warning: 5,
                alert: 5,
                operator: 'greaterThan',
                cdsTafVariableKey: 'CloudCoverOktas',
                cdsTafVariableLabel: 'CloudCover Oktas',
            },
        ],
    },
    Visibility: {
        primaryCdsTafVariableKey: 'VisibilityM',
        icon: 'moon-first-quarter',
        cdsTafVariables: [
            {
                unit: 'm',
                warning: 5000,
                alert: 2000,
                operator: 'lessThan',
                cdsTafVariableKey: 'VisibilityM',
                cdsTafVariableLabel: 'Visibility',
            },
        ],
    },
    Windspeed: {
        primaryCdsTafVariableKey: 'WindSpeedKt',
        icon: 'wind',
        cdsTafVariables: [
            {
                unit: 'kt',
                warning: 20,
                alert: 30,
                operator: 'greaterThan',
                cdsTafVariableKey: 'WindSpeedKt',
                cdsTafVariableLabel: 'WindSpeed',
            },
        ],
    },
    Windgust: {
        primaryCdsTafVariableKey: 'WindGustKt',
        icon: 'wind',
        cdsTafVariables: [
            {
                unit: 'kt',
                warning: 20,
                alert: 30,
                operator: 'greaterThan',
                cdsTafVariableKey: 'WindGustKt',
                cdsTafVariableLabel: 'WindGustKt',
            },
        ],
    },
    Crosswind: {
        primaryCdsTafVariableKey: 'CrossWindKt',
        icon: 'wind-southeast-light',
        cdsTafVariables: [
            {
                unit: 'kt',
                warning: 20,
                alert: 30,
                operator: 'greaterThan',
                cdsTafVariableKey: 'CrossWindKt',
                cdsTafVariableLabel: 'CrossWind',
            },
        ],
    },
    Lightning: {
        primaryCdsTafVariableKey: 'LightningPerc',
        icon: 'warning-thunderstorm',
        cdsTafVariables: [
            {
                unit: '%',
                warning: 10,
                alert: 20,
                operator: 'greaterThan',
                cdsTafVariableKey: 'LightningPerc',
                cdsTafVariableLabel: 'Lightning',
            },
        ],
    },
};
